import { type InjectionToken, type Provider, type Type } from '@angular/core';

import {
  REACTIVE_FORM_BUILDER_BROWSER,
  REACTIVE_FORM_BUILDER_DIAGNOSTICS,
  REACTIVE_FORM_BUILDER_ENVIRONMENT,
  REACTIVE_FORM_BUILDER_FILE_UPLOAD,
  REACTIVE_FORM_BUILDER_STORAGE,
  REACTIVE_FORM_BUILDER_TOUR,
  type ReactiveFormBuilderBrowserAdapter,
  type ReactiveFormBuilderDiagnosticsAdapter,
  type ReactiveFormBuilderEnvironment,
  type ReactiveFormBuilderFileUploadAdapter,
  type ReactiveFormBuilderStorageAdapter,
  type ReactiveFormBuilderTourAdapter,
} from '../adapters/reactive-form-builder.adapters';

export interface ReactiveFormBuilderUseExistingProvider<TAdapter> {
  readonly useExisting: Type<TAdapter>;
}

export interface ReactiveFormBuilderUseValueProvider<TAdapter> {
  readonly useValue: TAdapter;
}

export interface ReactiveFormBuilderUseFactoryProvider<TAdapter> {
  readonly useFactory: (...dependencies: any[]) => TAdapter;
  readonly deps?: unknown[];
}

export type ReactiveFormBuilderAdapterProvider<TAdapter> =
  | TAdapter
  | Type<TAdapter>
  | ReactiveFormBuilderUseExistingProvider<TAdapter>
  | ReactiveFormBuilderUseValueProvider<TAdapter>
  | ReactiveFormBuilderUseFactoryProvider<TAdapter>;

export interface FitogestReactiveFormBuilderProvidersConfig {
  readonly environment: ReactiveFormBuilderEnvironment;
  readonly storage: ReactiveFormBuilderStorageAdapter;
  readonly fileUpload: ReactiveFormBuilderAdapterProvider<ReactiveFormBuilderFileUploadAdapter>;
  readonly diagnostics?: ReactiveFormBuilderAdapterProvider<ReactiveFormBuilderDiagnosticsAdapter>;
  readonly tour?: ReactiveFormBuilderAdapterProvider<ReactiveFormBuilderTourAdapter>;
  readonly browser?: ReactiveFormBuilderAdapterProvider<ReactiveFormBuilderBrowserAdapter>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isUseExistingProvider<TAdapter>(
  source: ReactiveFormBuilderAdapterProvider<TAdapter>,
): source is ReactiveFormBuilderUseExistingProvider<TAdapter> {
  return isRecord(source) && 'useExisting' in source;
}

function isUseValueProvider<TAdapter>(
  source: ReactiveFormBuilderAdapterProvider<TAdapter>,
): source is ReactiveFormBuilderUseValueProvider<TAdapter> {
  return isRecord(source) && 'useValue' in source;
}

function isUseFactoryProvider<TAdapter>(
  source: ReactiveFormBuilderAdapterProvider<TAdapter>,
): source is ReactiveFormBuilderUseFactoryProvider<TAdapter> {
  return isRecord(source) && 'useFactory' in source;
}

function toAdapterProvider<TAdapter>(
  token: InjectionToken<TAdapter>,
  source: ReactiveFormBuilderAdapterProvider<TAdapter>,
): Provider {
  if (isUseExistingProvider(source)) {
    return {
      provide: token,
      useExisting: source.useExisting,
    };
  }

  if (isUseValueProvider(source)) {
    return {
      provide: token,
      useValue: source.useValue,
    };
  }

  if (isUseFactoryProvider(source)) {
    return {
      provide: token,
      useFactory: source.useFactory,
      deps: source.deps ?? [],
    };
  }

  if (typeof source === 'function') {
    return {
      provide: token,
      useExisting: source,
    };
  }

  return {
    provide: token,
    useValue: source,
  };
}

export function provideFitogestReactiveFormBuilder(config: FitogestReactiveFormBuilderProvidersConfig): Provider[] {
  const providers: Provider[] = [
    {
      provide: REACTIVE_FORM_BUILDER_ENVIRONMENT,
      useValue: config.environment,
    },
    {
      provide: REACTIVE_FORM_BUILDER_STORAGE,
      useValue: config.storage,
    },
    toAdapterProvider(REACTIVE_FORM_BUILDER_FILE_UPLOAD, config.fileUpload),
  ];

  if (config.diagnostics !== undefined) {
    providers.push(toAdapterProvider(REACTIVE_FORM_BUILDER_DIAGNOSTICS, config.diagnostics));
  }

  if (config.tour !== undefined) {
    providers.push(toAdapterProvider(REACTIVE_FORM_BUILDER_TOUR, config.tour));
  }

  if (config.browser !== undefined) {
    providers.push(toAdapterProvider(REACTIVE_FORM_BUILDER_BROWSER, config.browser));
  }

  return providers;
}
